import { useMemo } from "react";

import type { LangCode } from "@/lib/gql/__generated__/graphql";

import { useSendTestEmailRequestReservationConfirmed } from "@/components/features/items/edit/_shared/repository/mutation/send-sample-email/request-reservation/confirmed";
import { useSendTestEmailRequestReservationDeclined } from "@/components/features/items/edit/_shared/repository/mutation/send-sample-email/request-reservation/declined";
import { useSendTestEmailRequestReservationRequested } from "@/components/features/items/edit/_shared/repository/mutation/send-sample-email/request-reservation/requested";
import { useSendTestEmailReservationCin } from "@/components/features/items/edit/_shared/repository/mutation/send-sample-email/reservation/c-in";
import { useSendTestEmailReservationCanceled } from "@/components/features/items/edit/_shared/repository/mutation/send-sample-email/reservation/canceled";
import { useSendTestEmailReservationCreated } from "@/components/features/items/edit/_shared/repository/mutation/send-sample-email/reservation/created";
import { useSendTestEmailReservationUpdated } from "@/components/features/items/edit/_shared/repository/mutation/send-sample-email/reservation/updated";

import { useInitAccommodationHotelPolicyI18nFormModelContext } from "../../init";
import { useHotelPolicyI18nEditQuery } from "../../repository";
import { useUpdateHotelPolicyI18n } from "../use-update-hotel-policy-i18n";
import { serverModelToInputModelConverter } from "./converter/server-model-to-input-model";

import type { QueryData } from "../../type";

type Props = {
	hotelID: string;
	lang: LangCode;
};

export const useInitialized = ({ hotelID, lang }: Props) => {
	const serverModel = useHotelPolicyI18nEditQuery({ hotelID, lang });

	const inputModel = useMemo(() => serverModelToInputModelConverter({ serverModel }), [serverModel]);

	const queryData = {
		hotel: {
			hotelEmailAddress: serverModel.hotel.email ?? "",
		},
	} satisfies QueryData;

	const { onSetViewModel, viewModel, resetViewModel } = useInitAccommodationHotelPolicyI18nFormModelContext({
		inputModel,
		queryData,
	});

	const { onUpdateHotelPolicyI18n: onUpdate } = useUpdateHotelPolicyI18n({ viewModel, resetForm: resetViewModel });

	const { onSendTestEmailReservationCin } = useSendTestEmailReservationCin();
	const { onSendTestEmailReservationCanceled } = useSendTestEmailReservationCanceled();
	const { onSendTestEmailReservationCreated } = useSendTestEmailReservationCreated();
	const { onSendTestEmailReservationUpdated } = useSendTestEmailReservationUpdated();

	const { onSendTestEmailRequestReservationConfirmed } = useSendTestEmailRequestReservationConfirmed();
	const { onSendTestEmailRequestReservationDeclined } = useSendTestEmailRequestReservationDeclined();
	const { onSendTestEmailRequestReservationRequested } = useSendTestEmailRequestReservationRequested();

	return {
		queryData,
		viewModel,
		onSetViewModel,
		onUpdate,

		testEmailSender: {
			onSendTestEmailReservationCin,
			onSendTestEmailReservationCanceled,
			onSendTestEmailReservationCreated,
			onSendTestEmailReservationUpdated,

			onSendTestEmailRequestReservationConfirmed,
			onSendTestEmailRequestReservationDeclined,
			onSendTestEmailRequestReservationRequested,
		},
	};
};
